import { Button, TYPE } from '@common/Button/Button';
import { DeliveryInfo } from '@common/DeliveryInfo/DeliveryInfo';
import { Icon, IconColor, IconID } from '@common/Icon/Icon';
import { Link } from '@common/Link/Link';
import { Modal, ModalVariant } from '@common/Modal/Modal';
import { NumberInput } from '@common/NumberInput/NumberInput';
import { useProductPageStore } from '@hooks/states/useProductPageStore';
import { useSessionStore } from '@hooks/states/useSessionStore';
import { useBreakpoints } from '@hooks/useBreakpoints';
import { ToastType, useToasts } from '@hooks/useToasts';
import { useWishlist } from '@hooks/useWishlist';
import type { ProductAttributeValueAmountUnit } from '@libs/attribute.helper';
import type { BuyBoxConfiguration } from '@libs/buy-box';
import { getCuttingCostTranslationKeyByPriceType, showCopperSurchargeInfoBasedOnPriceCountry } from '@libs/buy-box';
import {
  addChainflexOverstockItemToShoppingCart,
  shoppingCartAddConfiguration,
  shoppingCartAddItems,
  updateSessionStore,
} from '@libs/catalog-service';
import { findMatchingCFCaseProducts } from '@libs/cfCase.helper';
import type { DeliveryTime } from '@libs/constants';
import {
  CHAINFLEX_MAX_BUY_QUANTITY,
  CHAINFLEX_MIN_BUY_QUANTITY,
  getLocale,
  LENGTH_AND_PIECE_WITHOUT_COPPER_MAX_BUY_QUANTITY,
  LENGTH_AND_PIECE_WITHOUT_COPPER_MIN_BUY_QUANTITY,
  VAT_CATEGORIES,
} from '@libs/constants';
import { calculateTotalProductPrice, displayPrice, getVATCalculatedPrice } from '@libs/product-price-utils';
import { openPopupWin } from '@libs/ui.helper';
import type { ShoppingCartItem } from '@models/api/shopping-cart.types';
import { ConfigurationType } from '@models/api/shopping-cart.types';
import { CFCaseSelectorModalContent } from '@product/BuyBox/modals/CFCaseSelectorModalContent';
import { CopperModalContent } from '@product/BuyBox/modals/CopperModalContent';
import { MyigusWishlistModalContent } from '@product/BuyBox/modals/MyigusWishlistModalContent';
import {
  ShoppingCartModalContent,
  ShoppingCartModalContentMode,
} from '@product/BuyBox/modals/ShoppingCartModalContent';
import { extractCFCaseProps } from '@product/ProductPage/PageLayouts/extractCFCaseProps';
import { useUniqueAssociatedProductsQuery } from '@product/ProductPage/PageLayouts/hooks/useUniqueAssociatedProductsQuery';
import { QuantityDiscount } from '@product/QuantityDiscount/QuantityDiscount';
import * as Sentry from '@sentry/nextjs';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { UseMutateFunction } from 'react-query';
import { useMutation } from 'react-query';
import { AddToEchainModalContent } from '../modals/AddToEchainModalContent';
import { BuyBoxModals } from '../modals/BuyBoxModals';
import { ESkinInformationModalContent } from '../modals/ESkinInformationModalContent';
import { getPriceCountry } from '@libs/get-price-country.helper';
import { useLengthUnitCodeForUnitSystem } from '@hooks/useLengthUnitCodeForUnitSystem';
import { getUnitLabel } from '@icommerce/units';
import type { NextRouter } from 'next/router';
import { useRouter } from 'next/router';
import { isVATEnabledForLocale } from '@icommerce/i18n/src';
import type { ChainflexOverstockSaleLocalesConfig } from './ChainflexOverstockSale/ChainflexOverstockSale';
import { ChainflexOverstockSale } from './ChainflexOverstockSale/ChainflexOverstockSale';
import { getRemoteConfig, useFeatureFlag } from '@libs/abby/abby';
import { CategoryHelper } from '@libs/category.helper';
import { EXTERNAL_LINKS } from '@libs/redirect.helper';
import { isPotentialMyIgusUser, useNewMyIgus } from '@libs/myigus';
import type { ServiceApiRequestError } from '@libs/service-api-request';
import type { CopperPrice } from '@models/graphql';
import { GraphQlPriceType } from '@models/graphql';
import { QueryStatusHandler } from '@common/QueryStatusHandler/QueryStatusHandler';
import { getProductLengthConfig } from '@libs/product.helper';

export interface BuyBoxProps {
  sku: string;
  iproProdID?: string;
  iproPrgrID?: string;
  copperIndex?: ProductAttributeValueAmountUnit;
  dailyCopperPriceDefinition?: CopperPrice[];
  deliveryTimeCode?: DeliveryTime;
  deliveryTimeText?: string;
  showAddToEchainConfiguration?: boolean;
  isEskinProduct?: boolean;
  chainLenghtCalculator?: (numGlieder: number) => number;
  chainNumLinksCalculator?: (length: number) => number;
  buyBoxConfiguration: BuyBoxConfiguration;
}

interface PriceDisplayBoxProps {
  totalCentDiscountPrice: number;
  currencyCode: string;
  locale?: string;
  vatEnabled?: boolean;
}

export const addItemToWishlist = (
  locale: string,
  router: NextRouter,
  addItemToWishListMutation: UseMutateFunction<unknown, ServiceApiRequestError, string>,
  sku: string,
  isNewMyIgus: boolean
) => {
  const isNotLoggedIn = !isPotentialMyIgusUser();

  if (isNotLoggedIn && isNewMyIgus) {
    window.location.assign(`${EXTERNAL_LINKS.NEW_MY_IGUS_LOGIN(locale)}?redirect=${encodeURIComponent(router.asPath)}`);
    return;
  }
  addItemToWishListMutation(sku);
};

export const PriceDisplayBox = ({ totalCentDiscountPrice, currencyCode, vatEnabled, locale }: PriceDisplayBoxProps) => {
  const { t, i18n } = useTranslation('product/buyBox');

  if (vatEnabled) {
    return (
      <>
        {displayPrice(getVATCalculatedPrice(locale, totalCentDiscountPrice), currencyCode, i18n.language, true)}
        <span data-testid="price-including-vat" className="mt-4 ml-2 text-gray-300 text-xxs">
          ({t('BUY_BOX.INCLUDING_VAT')})
        </span>
      </>
    );
  }

  return <>{displayPrice(totalCentDiscountPrice, currencyCode, i18n.language, true)}</>;
};

export const BuyBox = ({
  sku,
  iproProdID,
  iproPrgrID,
  copperIndex,
  dailyCopperPriceDefinition,
  deliveryTimeCode,
  deliveryTimeText,
  showAddToEchainConfiguration,
  isEskinProduct,
  buyBoxConfiguration,
  chainLenghtCalculator,
  chainNumLinksCalculator,
}: BuyBoxProps) => {
  const { sm } = useBreakpoints();
  const showOverstockSale = useFeatureFlag('showOverstockSale');
  const { t, i18n } = useTranslation(['product/buyBox', 'toast/toast', 'layout/header']);

  const router = useRouter();
  const locale = getLocale(router);
  const session = useSessionStore(state => state.session);
  const priceCountry = useMemo(() => getPriceCountry(locale, session), [locale, session]);
  const isNewMyIgus = useNewMyIgus(locale);

  const lengthUnitCodeForUnitSystem = useLengthUnitCodeForUnitSystem();
  const lengthUnitLabel = lengthUnitCodeForUnitSystem && getUnitLabel(lengthUnitCodeForUnitSystem);
  const unitSystem = useProductPageStore(state => state.unitSystem);

  const priceData = useProductPageStore(state => state.priceData);
  const currentProduct = useProductPageStore(state => state.currentProduct);

  const lengthConfig = getProductLengthConfig(
    priceData.priceType,
    currentProduct.attributes,
    buyBoxConfiguration.defaultLength,
    lengthUnitCodeForUnitSystem,
    unitSystem
  );

  const [pieces, setPieces] = useState(1);
  const [length, setLength] = useState(lengthConfig.default);
  const [numChainLinks, setNumChainLinks] = useState(buyBoxConfiguration.defaultNumChainLinks);

  const setPiecesAndLength = useProductPageStore(state => state.setPiecesAndLength);
  const { totalCentPrice, totalCentDiscount, totalCentDiscountPrice, unitCentDiscountPrice } =
    calculateTotalProductPrice(
      priceData?.priceType,
      Number(length),
      Number(pieces),
      Number(priceData?.basePricePerUnit),
      priceData?.additionalLengthPrice ? Number(priceData?.additionalLengthPrice) : undefined,
      priceData?.discountQuantity || undefined,
      lengthConfig,
      chainNumLinksCalculator ? chainNumLinksCalculator(length) : undefined,
      lengthUnitCodeForUnitSystem
    );

  const currentCategory = useProductPageStore(state => state.breadcrumbItems);

  const selectedChainflexOverstockItem = useProductPageStore(state => state.selectedChainflexOverstockItem);

  const isVatEnabled = useMemo(
    () => isVATEnabledForLocale(locale) && currentCategory.some(category => VAT_CATEGORIES.includes(category?.key)),
    [locale, currentCategory]
  );

  const canShowCopperSurchargeInfo = useMemo(
    () =>
      showCopperSurchargeInfoBasedOnPriceCountry(buyBoxConfiguration.showCopperSurchargeInfo, priceCountry, !!session),
    [buyBoxConfiguration.showCopperSurchargeInfo, priceCountry, session]
  );

  const [piecesInputHasErrors, setPiecesInputHasErrors] = useState(false);
  const [lengthInputHasErrors, setLengthInputHasErrors] = useState(false);
  const [numChainLinksInputHasErrors, setNumChainLinksInputHasErrors] = useState(false);
  const [addToWishlistModalContent, setAddToWishlistModalContent] = useState('');
  const [activeModal, setActiveModal] = useState<BuyBoxModals | null>(null);

  const { data: uniqueAssociatedProducts, isLoading: isLoadingUniqueAssociatedProducts } =
    useUniqueAssociatedProductsQuery();

  const matchingCFCaseProducts = useMemo(() => {
    if (isLoadingUniqueAssociatedProducts || !uniqueAssociatedProducts) {
      return undefined;
    }

    const { cfCases, cfCaseLengths } = extractCFCaseProps(currentProduct, uniqueAssociatedProducts);

    return findMatchingCFCaseProducts(currentProduct, cfCases, cfCaseLengths, length);
  }, [isLoadingUniqueAssociatedProducts, uniqueAssociatedProducts, currentProduct, length]);

  const totalProductPriceForCFOverstock = useMemo(
    () =>
      selectedChainflexOverstockItem
        ? calculateTotalProductPrice(
            priceData?.priceType,
            Number(selectedChainflexOverstockItem.chainflexMeterLength),
            Number(1),
            Number(priceData?.basePricePerUnit),
            priceData?.additionalLengthPrice ? Number(priceData?.additionalLengthPrice) : undefined,
            priceData?.discountQuantity || undefined,
            lengthConfig
          )
        : null,
    [selectedChainflexOverstockItem]
  );

  const { addToast } = useToasts();
  const openLogIn = (link: string) => openPopupWin(link, sm ? '_blank' : '_self', true, true, 1000, 800);
  const { mutate: addItemMutation } = useMutation(
    (articles: ShoppingCartItem[]) =>
      shoppingCartAddItems({
        articles,
      }),
    {
      onSuccess: async () => {
        setActiveModal(BuyBoxModals.ADD_TO_SHOPPING_CART);
        try {
          // Update session store after adding items to shopping cart.
          // Shopping cart item count in header should be updated automatically after state change
          await updateSessionStore();
        } catch (error) {
          Sentry.captureException(
            new Error(`Could not update session store after shopping cart update - ${error as string}`)
          );
        }
      },
      onError: () => {
        addToast({ content: t('TOAST.ERROR_OCCURRED', { ns: 'toast/toast' }), type: ToastType.ERROR });
      },
    }
  );
  const { mutate: addConfigurationMutation } = useMutation(
    async (articles: ShoppingCartItem[]) => {
      try {
        await shoppingCartAddConfiguration({
          configurationType: ConfigurationType.CFC,
          articles,
          quantity: 1,
        });
      } catch (error) {
        Sentry.captureException(new Error(`Could not add shopping cart configuration - ${error as string}`));
      }
    },
    {
      onSuccess: async () => {
        setActiveModal(BuyBoxModals.ADD_TO_SHOPPING_CART);
        try {
          // Update session store after adding items to shopping cart.
          // Shopping cart item count in header should be updated automatically after state change
          await updateSessionStore();
        } catch (error) {
          Sentry.captureException(new Error(`Could not queryClient.invalidateQueries - ${error as string}`));
        }
      },
      onError: () => {
        addToast({ content: t('TOAST.ERROR_OCCURRED', { ns: 'toast/toast' }), type: ToastType.ERROR });
      },
    }
  );

  const addItemToCart = () =>
    addItemMutation([
      {
        articleNumber: sku,
        quantity: pieces,
        length,
        unit: lengthUnitLabel,
      },
    ]);

  const addCFCaseConfigurationToCart = (cfcaseSku: string) =>
    addConfigurationMutation([
      {
        articleNumber: sku,
        quantity: pieces,
        length,
        unit: lengthUnitLabel,
      },
      { articleNumber: cfcaseSku, quantity: pieces },
    ]);

  const { mutate: addItemToWishListMutation } = useWishlist(
    setActiveModal,
    setAddToWishlistModalContent,
    openLogIn,
    sku
  );

  const canShowPrices =
    !!totalCentDiscountPrice &&
    totalCentDiscountPrice > 0 &&
    !!unitCentDiscountPrice &&
    unitCentDiscountPrice > 0 &&
    priceData?.currencyCode &&
    priceData?.fetchingDiscountPricesStatus !== 'error';
  const priceInputsHaveErrors =
    piecesInputHasErrors ||
    lengthInputHasErrors ||
    (buyBoxConfiguration.showNumChainLinksInput && numChainLinksInputHasErrors);

  // send request to add chainflex overstock item to shopping cart
  const addCFOverstockToShoppingCart = useCallback(async () => {
    const { articleNo, storageLocation, chainflexMeterLength } = selectedChainflexOverstockItem;
    try {
      await addChainflexOverstockItemToShoppingCart(locale, articleNo, storageLocation, chainflexMeterLength).then(
        async () => {
          setActiveModal(BuyBoxModals.ADD_TO_SHOPPING_CART);
          try {
            // Update session store after adding items to shopping cart.
            // Shopping cart item count in header should be updated automatically after state change
            await updateSessionStore();
          } catch (error) {
            Sentry.captureException(
              new Error(`Could not update session store after shopping cart update - ${error as string}`)
            );
          }
        }
      );
    } catch (error) {
      addToast({ content: t('TOAST.ERROR_OCCURRED', { ns: 'toast/toast' }), type: ToastType.ERROR });
    }
  }, [selectedChainflexOverstockItem]);

  const addToShoppingCart = () => {
    if (selectedChainflexOverstockItem) {
      void addCFOverstockToShoppingCart();
      return;
    }

    // show cfcase selection model if conditions are met
    if (matchingCFCaseProducts && matchingCFCaseProducts.length > 0) {
      setActiveModal(BuyBoxModals.ADD_CFCASE_CONFIGURATION_TO_SHOPPING_CART);
      return;
    }

    // show eskin notification dialog before adding item to shopping cart when conditions are met
    if (isEskinProduct) {
      setActiveModal(BuyBoxModals.SHOW_ESKIN_NOTIFICATION_BEFORE_SHOPPING_CART);
      return;
    }

    // else add Item to Cart
    addItemToCart();
  };

  useEffect(() => {
    if (!chainNumLinksCalculator || !chainLenghtCalculator) {
      return;
    }
    const ng = chainNumLinksCalculator(length);
    setNumChainLinks(ng);
    setLength(chainLenghtCalculator(ng));
  }, [currentProduct]);

  useEffect(() => {
    setLength(lengthConfig.default);
  }, [lengthConfig.min]);

  useEffect(() => {
    setPiecesAndLength({ pieces, length });
  }, [pieces, length, setPiecesAndLength]);

  const cuttingCostTranslationKey = getCuttingCostTranslationKeyByPriceType(priceData.priceType);

  const isOverflowSaleVisible =
    currentCategory.length > 1 &&
    CategoryHelper.isChainflex(currentCategory[currentCategory.length - 2].key) &&
    showOverstockSale;

  // checks if selectedChainflexOverstockItem is set and has valid prices
  const selectedChainflexOverstockItemHasPrices = useFeatureFlag('enableOverstockSaleOnDemandPrices')
    ? selectedChainflexOverstockItem?.unitPrice?.amount !== -1 &&
      selectedChainflexOverstockItem?.totalPrice?.amount !== -1
    : true;

  const chainflexOverstockSaleLocales = getRemoteConfig<'chainflexOverstockSaleLocales', 'JSON'>(
    'chainflexOverstockSaleLocales'
  ) as ChainflexOverstockSaleLocalesConfig;

  return (
    <div>
      <div className={classNames('bg-gray-50', 'p-8')} data-testid="buy-box-container">
        {buyBoxConfiguration.showPieceInput && (
          <NumberInput
            onChange={value => setPieces(value ?? 0)}
            dataTestId="pieces-input"
            value={selectedChainflexOverstockItem ? 1 : pieces}
            label={t('BUY_BOX.PIECES')}
            min={CHAINFLEX_MIN_BUY_QUANTITY}
            max={CHAINFLEX_MAX_BUY_QUANTITY}
            errorMessage={t('BUY_BOX.PIECES_INPUT_ERRORMESSAGE', {
              min: CHAINFLEX_MIN_BUY_QUANTITY,
              max: CHAINFLEX_MAX_BUY_QUANTITY,
            })}
            onError={errorType => setPiecesInputHasErrors(!!errorType)}
            step={1}
            onlyInt
            disabled={!!selectedChainflexOverstockItem}
            disabledTooltip={
              selectedChainflexOverstockItem && t('BUY_BOX.CHAINFLEX_OVERSTOCK_SALE.INPUT_FIELD_DISABLED')
            }
          />
        )}

        {isVatEnabled && canShowPrices && !priceInputsHaveErrors && (
          <span data-testid="price-per-unit" className="text-gray-300 text-xxs">
            {displayPrice(unitCentDiscountPrice, priceData?.currencyCode, i18n.language, true)} /{' '}
            {buyBoxConfiguration.discountUnitLabel} {t('BUY_BOX.PLUS_VAT')}
          </span>
        )}

        {buyBoxConfiguration.showQuantityInput && (
          <NumberInput
            value={pieces}
            dataTestId="quantity-input"
            onChange={value => setPieces(value ?? 0)}
            label={t('BUY_BOX.QUANTITY')}
            min={LENGTH_AND_PIECE_WITHOUT_COPPER_MIN_BUY_QUANTITY}
            max={LENGTH_AND_PIECE_WITHOUT_COPPER_MAX_BUY_QUANTITY}
            errorMessage={t('BUY_BOX.PIECES_INPUT_ERRORMESSAGE', {
              min: LENGTH_AND_PIECE_WITHOUT_COPPER_MIN_BUY_QUANTITY,
              max: LENGTH_AND_PIECE_WITHOUT_COPPER_MAX_BUY_QUANTITY,
            })}
            onError={errorType => setPiecesInputHasErrors(!!errorType)}
            step={1}
            onlyInt
          />
        )}
        {buyBoxConfiguration.showLengthInput && (
          <NumberInput
            onChange={value => setLength(value ?? 0)}
            onBlur={value => {
              if (buyBoxConfiguration.showNumChainLinksInput) {
                const ng = chainNumLinksCalculator(value);
                setNumChainLinks(ng);
                setLength(chainLenghtCalculator(ng));
              }
            }}
            dataTestId="length-input"
            value={selectedChainflexOverstockItem ? selectedChainflexOverstockItem.chainflexMeterLength : length}
            label={`${t('BUY_BOX.LENGTH')} (${lengthUnitLabel || ''})`}
            min={lengthConfig.min}
            max={lengthConfig.max}
            errorMessage={t(
              lengthConfig.onlyInt ? 'BUY_BOX.UNITS_INT_ONLY_INPUT_ERRORMESSAGE' : 'BUY_BOX.UNITS_INPUT_ERRORMESSAGE',
              {
                min: lengthConfig.min,
                max: lengthConfig.max,
              }
            )}
            onError={errorType => setLengthInputHasErrors(!!errorType)}
            onlyInt={lengthConfig.onlyInt}
            disabled={!!selectedChainflexOverstockItem}
            disabledTooltip={
              selectedChainflexOverstockItem && t('BUY_BOX.CHAINFLEX_OVERSTOCK_SALE.INPUT_FIELD_DISABLED')
            }
            decimalPlaces={
              [
                GraphQlPriceType.ChainLink,
                GraphQlPriceType.ChainLength,
                GraphQlPriceType.Length,
                GraphQlPriceType.LengthAndPiece,
              ].includes(priceData.priceType)
                ? 2
                : 0
            }
          />
        )}

        {buyBoxConfiguration.showNumChainLinksInput && (
          <NumberInput
            onChange={value => setLength(chainLenghtCalculator(value))}
            dataTestId="num-glieder-input"
            value={numChainLinks}
            label={t('BUY_BOX.NUM_GLIEDER')}
            min={1}
            max={9999}
            errorMessage={t(t('BUY_BOX.PIECES_INPUT_ERRORMESSAGE', {}), {
              min: 1,
              max: 9999,
            })}
            onError={errorType => setNumChainLinksInputHasErrors(!!errorType)}
            onlyInt
            step={1}
          />
        )}

        {isOverflowSaleVisible && chainflexOverstockSaleLocales.enabledLocales?.includes(locale) && (
          <ChainflexOverstockSale />
        )}

        <div className="my-8" data-testid="buy-box-prices-container">
          {!selectedChainflexOverstockItemHasPrices && (
            <>
              <span className="block text-xs">{t('BUY_BOX.PRICE')}: </span>
              <QueryStatusHandler status={priceData?.fetchingDiscountPricesStatus}>
                <span data-testid="total-price" className="text-4xl mt-1.5 block">
                  {t('BUY_BOX.PRICE_ON_REQUEST')}
                </span>
              </QueryStatusHandler>
            </>
          )}

          {selectedChainflexOverstockItemHasPrices && (
            <>
              <span className="block text-xs">
                {t('BUY_BOX.PRICE')}:{' '}
                {canShowPrices &&
                  !priceInputsHaveErrors &&
                  ((buyBoxConfiguration.showDiscountPrice && totalCentPrice !== totalCentDiscountPrice) ||
                    (selectedChainflexOverstockItem &&
                      selectedChainflexOverstockItem.totalPrice.amount * 100 !== totalCentPrice)) && (
                    <span className="line-through">
                      {displayPrice(
                        selectedChainflexOverstockItem
                          ? totalProductPriceForCFOverstock.totalCentPrice
                          : totalCentPrice,
                        priceData?.currencyCode,
                        i18n.language,
                        true
                      )}
                    </span>
                  )}
              </span>

              <QueryStatusHandler status={priceData?.fetchingDiscountPricesStatus}>
                <span data-testid="total-price" className="text-4xl mt-1.5 block">
                  {canShowPrices && !priceInputsHaveErrors && priceData?.fetchingDiscountPricesStatus !== 'loading' && (
                    <PriceDisplayBox
                      totalCentDiscountPrice={
                        selectedChainflexOverstockItem
                          ? selectedChainflexOverstockItem.totalPrice.amount * 100
                          : totalCentDiscountPrice
                      }
                      currencyCode={priceData?.currencyCode}
                      vatEnabled={isVatEnabled}
                      locale={locale}
                    />
                  )}
                  {((!canShowPrices && !priceInputsHaveErrors) ||
                    priceData?.fetchingDiscountPricesStatus === 'error') &&
                    t('BUY_BOX.PRICE_ON_REQUEST')}
                  {(priceInputsHaveErrors || priceData?.fetchingDiscountPricesStatus === 'loading') && '-'}
                </span>
              </QueryStatusHandler>
              {buyBoxConfiguration.showDiscountPrice &&
                canShowPrices &&
                !!totalCentDiscount &&
                !selectedChainflexOverstockItem && (
                  <span data-testid="total-discount" className="block my-4 text-xs">{`${t(
                    'BUY_BOX.TOTAL_DISCOUNT'
                  )}: ${displayPrice(totalCentDiscount, priceData?.currencyCode, i18n.language, true)}`}</span>
                )}
              {buyBoxConfiguration.showDiscountPrice && canShowPrices && selectedChainflexOverstockItem && (
                <span data-testid="total-discount" className="block my-4 text-xs font-bold text-igus-green">{`${t(
                  'BUY_BOX.CHAINFLEX_OVERSTOCK_SALE.DISCOUNT'
                )}: ${displayPrice(
                  totalProductPriceForCFOverstock.totalCentPrice -
                    selectedChainflexOverstockItem.totalPrice.amount * 100,
                  priceData?.currencyCode,
                  i18n.language,
                  true
                )}`}</span>
              )}
              {canShowPrices && (
                <span data-testid="unit-price" className="text-xs my-1.5 block">
                  {isVatEnabled ? (
                    <>
                      {displayPrice(unitCentDiscountPrice * pieces, priceData?.currencyCode, i18n.language, true)}
                      <span data-testid="price-excluding-vat" className="mt-4 ml-2 text-gray-300 text-xxs">
                        ({t('BUY_BOX.EXCLUDING_VAT')})
                      </span>
                    </>
                  ) : (
                    <>
                      {displayPrice(
                        selectedChainflexOverstockItem
                          ? selectedChainflexOverstockItem.unitPrice.amount * 100
                          : unitCentDiscountPrice,
                        priceData?.currencyCode,
                        i18n.language,
                        true
                      )}
                      <span data-testid="price-unit-label"> / {buyBoxConfiguration.discountUnitLabel}</span>
                    </>
                  )}
                </span>
              )}
            </>
          )}

          {priceData?.discountQuantity && !selectedChainflexOverstockItem && (
            <QuantityDiscount
              sku={sku}
              unitLabel={buyBoxConfiguration.discountUnitLabel}
              copperIndex={copperIndex}
              dailyCopperPriceDefinition={dailyCopperPriceDefinition}
              showCopperSurchargeInfo={canShowCopperSurchargeInfo}
              showCuttingCostInfo={buyBoxConfiguration.showCuttingCostInfo}
            />
          )}
          <p className="mt-4 text-gray-300 text-xxs" data-testid="additional-costs">
            {copperIndex && canShowCopperSurchargeInfo && (
              <span className="block">{t('BUY_BOX.PRICE_PLUS_COPPER_INFO_SHORTCUT')}</span>
            )}
            {buyBoxConfiguration.showCuttingCostInfo && !selectedChainflexOverstockItem && (
              <span className="block" dangerouslySetInnerHTML={{ __html: t(cuttingCostTranslationKey) }} />
            )}
            <span className="block">{t('BUY_BOX.PRICE_PLUS_TAX_AND_SHIPPING')}</span>
          </p>
          {copperIndex && canShowCopperSurchargeInfo && (
            <Link
              dataTestId="calculate-copper-surcharge-link"
              className="text-gray-500"
              onClick={() => setActiveModal(BuyBoxModals.SHOW_COPPER_CALCULATOR)}
              icon={IconID.ARROW_RIGHT}
              text={t('BUY_BOX.CALCULATE_COPPER_SURCHARGE')}
            />
          )}
        </div>

        <DeliveryInfo deliveryTimeCode={deliveryTimeCode} deliveryTimeText={deliveryTimeText} />

        <Button
          icon={<Icon size={10} color={IconColor.WHITE} id={IconID.CART} />}
          text={t('BUY_BOX.ADD_TO_CART', { ns: 'product/buyBox' })}
          type={TYPE.PRIMARY}
          onClick={() => addToShoppingCart()}
          disabled={
            piecesInputHasErrors ||
            lengthInputHasErrors ||
            (priceData.priceType === GraphQlPriceType.ChainLink && numChainLinksInputHasErrors)
          }
        />
        <Link
          dataTestId="add-to-wishlist-link"
          text={t('BUY_BOX.ADD_TO_WISHLIST')}
          onClick={() => addItemToWishlist(locale, router, addItemToWishListMutation, sku, isNewMyIgus)}
          primary={false}
          icon={IconID.REMINDER_LIST}
          iconPrimary={false}
          bigIcon
          className="items-center mt-6"
        />
        {showAddToEchainConfiguration && (
          <>
            <Link
              text={t('BUY_BOX.ADD_TO_ECHAIN_CONFIGURATION')}
              onClick={() => setActiveModal(BuyBoxModals.ADD_TO_ECHAIN)}
              primary={false}
              icon={IconID.ARROW_RIGHT}
              iconPrimary
              className="items-center mt-6"
            />
            <Modal visible={activeModal === BuyBoxModals.ADD_TO_ECHAIN} onClose={() => setActiveModal(null)}>
              <AddToEchainModalContent sku={sku} />
            </Modal>
          </>
        )}

        <Modal
          visible={activeModal === BuyBoxModals.ADD_CFCASE_CONFIGURATION_TO_SHOPPING_CART}
          onClose={() => setActiveModal(null)}
        >
          <CFCaseSelectorModalContent
            cfCases={matchingCFCaseProducts}
            onAddCFCaseConfigurationToShoppingCart={newSku =>
              newSku ? addCFCaseConfigurationToCart(newSku) : addItemToCart()
            }
          />
        </Modal>
        <Modal
          visible={activeModal === BuyBoxModals.SHOW_ESKIN_NOTIFICATION_BEFORE_SHOPPING_CART}
          onClose={() => setActiveModal(null)}
        >
          <ESkinInformationModalContent onAddToShoppingCart={addItemToCart} />
        </Modal>
        <Modal
          variant={ModalVariant.ALERT}
          visible={activeModal === BuyBoxModals.ADD_TO_SHOPPING_CART}
          onClose={() => setActiveModal(null)}
          headline={t('FALLBACK_HEADER.SHOPPINGCART', { ns: 'layout/header' })}
        >
          <ShoppingCartModalContent
            onClose={() => setActiveModal(null)}
            mode={ShoppingCartModalContentMode.ADD_ARTICLE}
          />
        </Modal>
        <Modal visible={activeModal === BuyBoxModals.SHOW_COPPER_CALCULATOR} onClose={() => setActiveModal(null)}>
          <CopperModalContent sku={sku} iproProdID={iproProdID} iproPrgrID={iproPrgrID} />
        </Modal>
        <Modal visible={activeModal === BuyBoxModals.ADD_TO_MYIGUS_WISH_LIST} onClose={() => setActiveModal(null)}>
          <MyigusWishlistModalContent content={addToWishlistModalContent} />
        </Modal>
      </div>
    </div>
  );
};
